/**
 * Session lifecycle wiring for the Observ RUM SDK (Kapitalvins parity).
 *
 * `session.ts` owns the pure rotation logic (lazy, no timers, no I/O). THIS module
 * turns those transitions into observable signals and drives them in real time:
 *
 *   - emits `session.start` (with `session.previous_id` on a continuation) and
 *     `session.end` (with `session.duration_ms`) as OTel **log events** on the
 *     shared `/v1/logs` channel — by subscribing to {@link addSessionListener},
 *     so the events fire from the single code path that mints/ends a session,
 *     never from a scattered call site;
 *   - resets the throttled `session.id` cache the instant a rotation happens, so
 *     post-rotation events carry the new id;
 *   - treats user input (`click`/`keydown`/`scroll`/`mousemove`) as activity that
 *     refreshes the inactivity window (and triggers a lazy rotation when due);
 *   - runs a periodic **sweep** so an idle session still produces a timely
 *     `session.end` even with no further events to trigger a lazy rotation;
 *   - flushes a best-effort `session.end` on `pagehide`/`visibilitychange:hidden`
 *     (the browser can't guarantee it on a crash — duration is derived backend
 *     side, this is the at-best signal);
 *   - when `crossTab` is enabled, adopts the session another tab just wrote.
 *
 * Like the rest of the SDK it is best-effort and never throws to the host page.
 * The shared logs provider is shut down once by `index.shutdown()` (it is also
 * used by semantic-events / js-errors), never here.
 */
import type { Logger } from '@opentelemetry/api-logs'

import { emitSessionEvent, resetSessionIdCache, setupOtelLogs } from './otel-logs.js'
import { SESSION_ID_ATTRIBUTE } from './constants.js'
import {
  addSessionListener,
  adoptStoredSession,
  endSessionIfExpired,
  SESSION_STORAGE_KEY,
  touchSession,
  type SessionLifecycleEvent,
} from './session.js'
import type { ObservInitOptions } from './types.js'

/** Canonical `event.name` values for the session lifecycle events. */
export const SESSION_START_EVENT = 'session.start'
export const SESSION_END_EVENT = 'session.end'

/** Sweep cadence: how often we check the active session for idle expiry. */
export const SWEEP_INTERVAL_MS = 60_000
/** Throttle for the high-frequency `mousemove` activity signal. */
const MOUSEMOVE_THROTTLE_MS = 1_000

/** DOM events treated as user activity (each refreshes the inactivity window). */
const ACTIVITY_EVENTS = ['click', 'keydown', 'scroll'] as const

/** Public handle for the running session-lifecycle wiring. */
export interface SessionLifecycleHandle {
  /** Remove listeners + sweep timer and unsubscribe. Idempotent. */
  stop(): void
}

/** Module-level guard so the wiring is self-idempotent (one per page). */
let active: SessionLifecycleHandle | null = null

/**
 * Translate a {@link SessionLifecycleEvent} into a `session.start` / `session.end`
 * log event. The `session.id` is passed explicitly (overriding the throttled one
 * that {@link emitSessionEvent} would inject) so the event always carries the id
 * of the session it describes, even within the post-rotation throttle window.
 */
function emitLifecycle(logger: Logger, event: SessionLifecycleEvent): void {
  if (event.type === 'start') {
    // A rotation just minted this id — drop the stale cache so later events read it.
    resetSessionIdCache()
    const attrs: Record<string, string> = { [SESSION_ID_ATTRIBUTE]: event.id }
    if (event.previousId) attrs['session.previous_id'] = event.previousId
    emitSessionEvent(logger, SESSION_START_EVENT, attrs)
  } else {
    emitSessionEvent(logger, SESSION_END_EVENT, {
      [SESSION_ID_ATTRIBUTE]: event.id,
      'session.duration_ms': String(event.durationMs),
    })
  }
}

/**
 * Start the session lifecycle wiring. Returns a handle whose `stop()` tears
 * everything down. Subscribes the lifecycle listener BEFORE the caller bootstraps
 * the session (`ensureSession` in `index.init`) so the very first `session.start`
 * is captured. Never throws; degrades to a no-op without a DOM or a logs channel.
 */
export function startSessionLifecycle(options: ObservInitOptions): SessionLifecycleHandle {
  if (active) return active

  let logger: Logger | null = null
  try {
    logger = setupOtelLogs(options)
  } catch (err) {
    // Without a logger we can't emit start/end, but activity tracking + rotation
    // still matter — keep going with a null logger (emit becomes a no-op).
    console.warn('[observ] session lifecycle: logs setup failed; events disabled.', err)
  }

  const unsubscribe = addSessionListener((event) => {
    if (logger) emitLifecycle(logger, event)
  })

  // No DOM (SSR / worker): keep the subscription so a programmatic ensureSession
  // still emits start/end, but skip the browser-only listeners + timers.
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    const handle: SessionLifecycleHandle = {
      stop() {
        unsubscribe()
        active = null
      },
    }
    active = handle
    return handle
  }

  const onActivity = (): void => {
    try {
      touchSession()
    } catch {
      /* never let an activity listener break the host page */
    }
  }

  // Throttle the firehose `mousemove` so a drag can't hammer touchSession.
  let lastMove = 0
  const onMouseMove = (): void => {
    const now = Date.now()
    if (now - lastMove < MOUSEMOVE_THROTTLE_MS) return
    lastMove = now
    onActivity()
  }

  for (const evt of ACTIVITY_EVENTS) {
    window.addEventListener(evt, onActivity, { passive: true })
  }
  window.addEventListener('mousemove', onMouseMove, { passive: true })

  // Best-effort end on background / unload (the browser can't guarantee it).
  const onHide = (): void => {
    try {
      endSessionIfExpired()
    } catch {
      /* swallow */
    }
  }
  const onVisibility = (): void => {
    if (document.visibilityState === 'hidden') onHide()
  }
  window.addEventListener('pagehide', onHide)
  document.addEventListener('visibilitychange', onVisibility)

  // Cross-tab: adopt the session another tab just wrote (only fires across
  // documents, and only when `crossTab` switched the store to localStorage).
  const onStorage = (e: StorageEvent): void => {
    if (e.key !== SESSION_STORAGE_KEY || e.newValue == null) return
    try {
      adoptStoredSession()
      resetSessionIdCache()
    } catch {
      /* swallow */
    }
  }
  window.addEventListener('storage', onStorage)

  // Periodic sweep: detect idle expiry without waiting for the next event/read.
  const sweepTimer = setInterval(() => {
    try {
      endSessionIfExpired()
    } catch {
      /* swallow */
    }
  }, SWEEP_INTERVAL_MS)

  let stopped = false
  const handle: SessionLifecycleHandle = {
    stop() {
      if (stopped) return
      stopped = true
      active = null
      unsubscribe()
      clearInterval(sweepTimer)
      for (const evt of ACTIVITY_EVENTS) window.removeEventListener(evt, onActivity)
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('pagehide', onHide)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('storage', onStorage)
    },
  }
  active = handle
  return handle
}
